import type { ElementClickedEvent } from '../../../../../types';
import type { MouseEvent } from 'react';

import EVENT_NAMES from '../../../../../constants/generated/event-names';
import debounce from 'lodash/debounce';
import { useAnalytics } from '@blueshift-ui/analytics';
import { useCallback } from 'react';

interface UseDebounceTrackMenuParams {
  navSlug?: string;
}

const useDebounceTrackMenu = ({ navSlug }: UseDebounceTrackMenuParams) => {
  const analytics = useAnalytics();

  /**
   * This tracks interactions with the site nav.
   *
   * It debounces the event to prevent spamming the analytics.
   * It also handles the case where the target is an image with an alt tag.
   *
   * There are some cases where the element_text will be empty, such as in the search input. These
   * will have to be handled in the future.
   */
  const trackMenuAction = useCallback(
    (action: string) => (event: MouseEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const elementText =
        target.closest('.MuiMenuItem-root, .MuiButtonBase-root')?.textContent ??
        (target.closest('[alt]') as HTMLImageElement)?.alt ??
        '';

      analytics.track(
        action === 'click' ? EVENT_NAMES.elementClicked : EVENT_NAMES.elementHovered,
        {
          element_id: 'site_nav',
          element_type: 'button',
          element_text: elementText,
          metadata: {
            nav_slug: navSlug,
          },
          page_path: window.location.pathname,
          page_section: 'site_nav',
        } satisfies ElementClickedEvent
      );
    },
    [analytics, navSlug]
  );

  const debouncedTrackMenuHover = debounce(trackMenuAction('hover'), 800);
  const debouncedTrackMenuClick = debounce(trackMenuAction('click'), 810);

  return { debouncedTrackMenuHover, debouncedTrackMenuClick };
};

export default useDebounceTrackMenu;
